import {
    ColDef,
    ColumnMovedEvent,
    ColumnResizedEvent,
    ICellRendererParams,
    RowClickedEvent
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import React, { RefObject, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { setupGrid } from '../../common/datagrid/agGridUtils';
import { useAgTable } from '../../common/datagrid/hooks/useAgTable';
import '../../common/datagrid/table-style.css';
import { AgreementDto } from '../../common/PricePrgClient';
import { RootState } from '../../store';
import { filterByValue } from '../../utils';
import { toDateString } from '../../utils/dateUtils';
import { AgreementStatusIcon } from './AgreementStatusIcon';
import { getAgreementTable, getAgreementTableRows, getSelectedAgreementId } from './redux/agreementSelectors';
import { agreementSelected, agreementTableActions } from './redux/agreementSlices';

const mapStateToProps = (state: RootState) => ({
    selectedAgreementId: getSelectedAgreementId(state),
    agreementTable: getAgreementTable(state),
    agreementRows: getAgreementTableRows(state)
});
type Props = ReturnType<typeof mapStateToProps> & {
    searchFilter: string;
};

const colsBasicData: ColDef<AgreementDto>[] = [
    {
        field: 'name',
        headerName: 'Name',
        resizable: true,
        floatingFilter: true,
        floatingFilterComponentParams: {
            suppressFilterButton: true
        },
        filter: 'agTextColumnFilter',
        cellRenderer: ({ data: row }: ICellRendererParams<AgreementDto>) => {
            if (row == null) {
                return React.Fragment;
            }
            return (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <AgreementStatusIcon agreementStatus={row.agreementStatus} />
                    <span style={{ paddingLeft: 4 }}>{row.name}</span>
                </div>
            );
        },
        width: 152
    },
    {
        field: 'validTo',
        headerName: 'Date',
        resizable: true,
        floatingFilter: true,
        floatingFilterComponentParams: {
            suppressFilterButton: true
        },
        filter: 'agTextColumnFilter',
        cellRenderer: ({ data: row }: ICellRendererParams<AgreementDto>) => {
            if (row == null) {
                return React.Fragment;
            }
            return (
                <>{`${toDateString(new Date(row.validFrom), 'dd/MM')} - ${toDateString(
                    new Date(row.validTo),
                    'dd/MM'
                )}`}</>
            );
        },
        width: 107
    },
    {
        field: 'createdBy',
        headerName: 'Created by',
        resizable: true,
        floatingFilter: true,
        floatingFilterComponentParams: {
            suppressFilterButton: true
        },
        filter: 'agTextColumnFilter',
        width: 99
    }
];

const columns: ColDef<AgreementDto>[] = colsBasicData.map(c => ({
    ...c,
    sortable: false,
    resizable: true
}));
let gridRef = {} as RefObject<AgGridReact>;
// function rowKeyGetter(row: AgreementDto) {
//     return row.agreementId;
// }

const AgreementList = ({ agreementRows, searchFilter, agreementTable }: Props) => {
    const dispatch = useDispatch();
    gridRef = useRef<AgGridReact>(null);

    const {
        // filterState: [filters, setFilters],
        columns: cols,
        saveColumnWidth
    } = useAgTable({
        columns: columns,
        rows: agreementRows,
        tableActions: agreementTableActions,
        tableSettings: agreementTable,
        gridRef: gridRef
    });

    const handleRowClick = (event: RowClickedEvent<AgreementDto>) => {
        if (event.data != null) dispatch(agreementSelected(event.data.agreementId));
    };

    const [, setDraggedColumn] = useState(null as { colId: string; toIndex: number } | null);
    const filteredRows = useMemo(() => {
        return filterByValue(agreementRows, searchFilter);
    }, [searchFilter, agreementRows]);
    return (
        <AgGridReact<AgreementDto>
            className="ag-theme-alpine"
            columnDefs={cols}
            rowData={filteredRows}
            ref={gridRef}
            containerStyle={{ height: '82vh' }}
            headerHeight={25}
            floatingFiltersHeight={30}
            rowHeight={25}
            onRowClicked={event => handleRowClick(event)}
            onFirstDataRendered={setupGrid}
            onColumnMoved={(event: ColumnMovedEvent<AgreementDto>) => {
                setDraggedColumn({ colId: event.column?.getColId() ?? '', toIndex: event.toIndex ?? 0 });
            }}
            onColumnResized={(event: ColumnResizedEvent<AgreementDto>) => {
                if (event.finished && event.source == 'uiColumnDragged' && event.type == 'columnResized') {
                    const colState = event.columnApi.getColumnState();

                    const indexOfCol = colState.findIndex(c => c.colId == event.column?.getColId());
                    saveColumnWidth(indexOfCol, colState[indexOfCol]?.width ?? 80);
                }
            }}
        />
    );
};
export default connect(mapStateToProps)(AgreementList);
